<template>
  <div v-if="hsl" class="color-picker">
    <label class="font-bold text-lg">{{ label }}</label>
    <div class="flex items-center flex-wrap gap-2 mb-3">
      <p
        class="w-5 h-5 rounded-full"
        :style="{ backgroundColor: hexColor }"
      ></p>
      <InputText
        v-model="hexColor"
        :maxlength="9"
        :minlength="7"
        class="xl:!w-4/12 !h-10"
        required
      />
      <div
        class="flex items-center justify-center rounded-lg overflow-hidden border w-full xl:w-fit select-none text-h-neutral-500 cursor-not-allowed"
      >
        <p class="text-xs p-3">{{ "H: " + hsl.h }}</p>
        <p class="text-xs p-3 border">{{ "S: " + hsl.s }}</p>
        <p class="text-xs p-3">{{ "L: " + hsl.l }}</p>
      </div>
    </div>

    <div class="sliders flex flex-col gap-5">
      <Slider
        v-model="hsl.h"
        :min="0"
        :max="360"
        class="custom-hue"
        @update:modelValue="updateHSL"
      />
      <Slider
        v-model="hsl.s"
        :min="0"
        :max="100"
        class="custom-saturation"
        @update:modelValue="updateHSL"
      />
      <Slider
        v-model="hsl.l"
        :min="0"
        :max="100"
        class="custom-lightness"
        @update:modelValue="updateHSL"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { hexToHSL, hslToHex } from "@/utils/theme.util";
import { computed, defineComponent, ref, watch } from "vue";

export default defineComponent({
  name: "ThemeColorPicker",
  emits: ["update:modelValue"],
  props: {
    modelValue: {
      type: String,
      default: "",
    },
    label: String,
  },
  setup(props, { emit }) {
    const hsl = ref(hexToHSL(props.modelValue));

    const hexColor = computed({
      get: () => hslToHex(hsl.value!.h, hsl.value!.s, hsl.value!.l),
      set: (newHex: string) => {
        if (isValidHex(newHex)) {
          const newHSL = hexToHSL(newHex);
          if (newHSL) hsl.value = newHSL;
          emit("update:modelValue", newHex);
        } else {
          console.error(`Invalid hex color: ${newHex}`);
        }
      },
    });

    const isValidHex = (hex: string) => {
      return /^#[0-9A-Fa-f]{6}$/.test(hex);
    };

    const updateHSL = () => {
      const newHex = hslToHex(hsl.value!.h, hsl.value!.s, hsl.value!.l);
      emit("update:modelValue", newHex);
    };

    watch(
      () => props.modelValue,
      (newVal) => {
        if (isValidHex(newVal)) {
          hsl.value = hexToHSL(newVal);
        } else {
          console.error(`Invalid hex color: ${newVal}`);
        }
      }
    );

    return { hexColor, hsl, updateHSL };
  },
});
</script>

<style lang="scss">
.color-picker {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.sliders label {
  font-size: 14px;
  font-weight: bold;
}

.p-slider-range {
  background-color: transparent !important;
  height: 50px !important;
}

.p-slider {
  position: relative;
  height: 8px;
  border-radius: 8px;
}

.p-slider-handle {
  z-index: 1;
  background: white !important;
  @apply border-4 border-h-neutral-400 shadow-lg #{!important};
}

.custom-hue::before {
  content: "";
  position: absolute;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    to right,
    red,
    yellow,
    lime,
    cyan,
    blue,
    magenta,
    red
  );
  border-radius: 8px;
  z-index: 0;
}

.custom-saturation::before {
  content: "";
  position: absolute;
  width: 100%;
  height: 100%;
  background: linear-gradient(to right, gray, green);
  border-radius: 8px;
  z-index: 0;
}

.custom-lightness::before {
  content: "";
  position: absolute;
  width: 100%;
  height: 100%;
  background: linear-gradient(to right, black, white);
  border-radius: 8px;
  z-index: 0;
}
</style>
